import { reduceWithSum } from "../../utils/arrayUtils";
import { ReportingAgentStates, ReportingMediaType, ReportingMediaTypes } from "../models/reports";
import { AgentReport, TeamReport, WorkItemHandlingStats } from "../models/reports.agents";
import { Team } from "../models/team";
import { defaultAgentReport } from "./teamCalculations";

function getHandlingStatByBusinessUnit(members: AgentReport[], businessUnitId: string): WorkItemHandlingStats {
  const handledCount = reduceWithSum(members, ar => ar.workItemsByBusinessUnit[businessUnitId]?.handledCount ?? 0);
  const missedCount = reduceWithSum(members, ar => ar.workItemsByBusinessUnit[businessUnitId]?.missedCount ?? 0);
  const rejectedCount = reduceWithSum(members, ar => ar.workItemsByBusinessUnit[businessUnitId]?.rejectedCount ?? 0);
  const sumHandlingTime = reduceWithSum(members, ar => (ar.workItemsByBusinessUnit[businessUnitId]?.averageHandlingTimeInSeconds ?? 0) * (ar.workItemsByBusinessUnit[businessUnitId]?.handledCount ?? 0));

  return {
    handledCount,
    missedCount,
    rejectedCount,
    averageHandlingTimeInSeconds: handledCount > 0 ? Math.floor(sumHandlingTime / handledCount) : 0,
  };
}

function getHandlingStatByMedia(members: AgentReport[], media: ReportingMediaType): WorkItemHandlingStats {
  const handledCount = reduceWithSum(members, ar => ar.workItemsByMedia[media].handledCount);
  const missedCount = reduceWithSum(members, ar => ar.workItemsByMedia[media].missedCount);
  const rejectedCount = reduceWithSum(members, ar => ar.workItemsByMedia[media].rejectedCount);
  const sumHandlingTime = reduceWithSum(members, ar => ar.workItemsByMedia[media].averageHandlingTimeInSeconds * ar.workItemsByMedia[media].handledCount);

  return {
    handledCount,
    missedCount,
    rejectedCount,
    averageHandlingTimeInSeconds: handledCount > 0 ? Math.floor(sumHandlingTime / handledCount) : 0,
  };
}

export function aggregateAgentProperties(agents: AgentReport[], selector: (agent: AgentReport) => Record<string, number>): Record<string, number> {
  const collector: Record<string, number> = agents.length > 0 ? {} : selector(defaultAgentReport(""));

  agents.forEach(a =>
    Object.entries(selector(a))
      .map(([key, value]) => { collector[key] = (collector[key] ?? 0) + value; }),
  );

  return collector;
}

export default function aggregateAgentsIntoTeam(team: Team, agents: AgentReport[]): TeamReport {
  const members = agents.filter(a => team.memberIds.includes(a.id));

  const businessUnitIds = Array.from(
    new Set(
      members.flatMap(m => Object.keys(m.workItemsByBusinessUnit)),
    ),
  );

  return {
    id: team.id,
    name: team.name,
    memberIds: team.memberIds,
    memberReports: members,
    currentMemberAvailability: ReportingAgentStates.reduce((coll, state) => ({ ...coll, [state]: members.filter(m => m.currentState === state).length }), {} as TeamReport["currentMemberAvailability"]),
    timeSpentInStates: aggregateAgentProperties(members, m => m.timeSpentInStates),
    timeSpentInBreakNames: aggregateAgentProperties(members, m => m.timeSpentInBreakNames),
    workItemsByBusinessUnit: businessUnitIds.reduce((coll, buId) => ({ ...coll, [buId]: getHandlingStatByBusinessUnit(members, buId) }), {} as TeamReport["workItemsByBusinessUnit"]),
    workItemsByMedia: ReportingMediaTypes.reduce((coll, media) => ({ ...coll, [media]: getHandlingStatByMedia(members, media) }), {} as TeamReport["workItemsByMedia"]),
    workItemsByDispositionCode: aggregateAgentProperties(members, m => m.workItemsByDispositionCode),
  };
}
